import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { RawIntlProvider, createIntl } from 'react-intl';
import { ConfigProvider } from 'antd';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import enGB from '../utility/lang/en-GB';
import esES from '../utility/lang/es-ES';
import enUS from '../utility/lang/en-US';
import { selectSessionUser } from '../state/ui/application/sessionUiReducer';
import viaIntl, { cache } from './via-intl';
import { selectTranslations } from '../state/domain/appConfig/translationsReducer';
import { requestLoadTranslations } from '../state/ui/application/translationUiAction';
import { setLanguageCookie } from '../utility/cookie-utility';
import { useSessionBootstrap } from '../components/routes/ViaSecuredRoute';
import { getDefaultLocale } from "../configWeb";


export const ViaLocaleContext = React.createContext({
  appLocale: getDefaultLocale(),
  toggleLocale: () => {},
});


function useLocale() {
  const [appLocale, setAppLocale] = useState(null); // This now defaults to null since we do not want the app to load with english straight away.

  const toggleLocale = useCallback((langCode) => {
    switch (langCode) {
      case 'es_ES':
        setAppLocale(esES);
        break;
      case 'en_GB':
        setAppLocale(enGB);
        break;
      case 'en_US':
        setAppLocale(enUS);
        break;
      default:
        setAppLocale(getDefaultLocale());
        break;
    }
  }, [setAppLocale]);

  return [appLocale, toggleLocale];
}

/**
 * Grouping of the language context providers for the VIA application.
 * - ViaLocaleContext - Provides the current context and the ability to update it.
 * - LocaleProvider - Language context for ant components.
 * - IntlProvider - Language context for our translation keys.
 *
 * @param {Component} props.children The children supplied for this component.
 */
export default function ViaLanguageProvider({ children }) {
  // Redux State
  const translations = useSelector(selectTranslations);
  const sessionUser = useSelector(selectSessionUser);

  // Redux Actions
  const dispatch = useDispatch();
  const loadTranslations = useCallback(locale => dispatch(requestLoadTranslations(locale)), [dispatch]);

  // React State
  const isSessionInitialised = useSessionBootstrap();
  const [currentIntl, setCurrentIntl] = useState(viaIntl.intl); // Made the Intl stateful as it is easier to keep track of this way.
  const [appLocale, toggleLocale] = useLocale();

  useEffect(() => {
    if (isSessionInitialised) toggleLocale(sessionUser ? sessionUser.locale : getDefaultLocale());
  }, [isSessionInitialised, sessionUser, toggleLocale]);

  // download translations when the locale changes
  // Also update the language cookie so that the server knows.
  useEffect(() => {
    if (appLocale) {
      loadTranslations(appLocale.code);
      setLanguageCookie(appLocale.locale);
      moment.locale(appLocale.code);
    }
  }, [appLocale, loadTranslations]);

  // The next two methods are split to prevent to app from changing its translations before the translation file has loaded.
  const updateIntl = useCallback((updatedTranslations) => {
    if (!appLocale) return;

    const newIntl = createIntl({ locale: appLocale.locale, messages: updatedTranslations[appLocale.code] }, cache);
    viaIntl.intl = newIntl;
    setCurrentIntl(newIntl);
  }, [appLocale]);

  // Make sure intl is using the newest version of the translation field
  // Also load the translations if they haven't been loaded.
  // (This was to solve an odd bug caused by the state being reset upon not finding a session user.)
  useEffect(() => {
    if (translations && translations[appLocale.code]) updateIntl(translations);
    else if (appLocale) loadTranslations(appLocale.code);
  }, [translations, appLocale, updateIntl, loadTranslations]);

  // Only load the application once we have checked for a session user
  const hasTranslations = !!(translations && currentIntl.messages && Object.keys(currentIntl.messages).length);
  if (!isSessionInitialised || !hasTranslations || !appLocale) return <></>;

  return (
    <ViaLocaleContext.Provider value={{ appLocale, toggleLocale }}>
      <ConfigProvider locale={appLocale.antd}>
        <RawIntlProvider value={currentIntl}>
          {children}
        </RawIntlProvider>
      </ConfigProvider>
    </ViaLocaleContext.Provider>
  );
}

// Validate the prop type parameters.
ViaLanguageProvider.propTypes = {
  children: PropTypes.element.isRequired,
  sessionUserLoaded: PropTypes.shape({
    isLoading: PropTypes.bool.isRequired,
  }),
};

ViaLanguageProvider.defaultProps = {
  sessionUserLoaded: null,
};
